import { AssessmentQuestion, AnswerData } from "@/components/assessment/conversational/types";
import { isSelectAnswer, isTextAnswer, isDateSliderAnswer } from "@/components/assessment/conversational/answerTypes";
import { evaluateConditionalDisplay, isCompoundCondition } from "./useConditionEvaluation";

/**
 * Evaluates conditional display rules against an answer value.
 * Supports string answers, array (multi-select) answers, and __not: prefix.
 */
export function checkConditionMatch(showWhen: string[], answer: any): boolean {
  if (answer === undefined || answer === null) return false;

  const notConditions = showWhen.filter(c => c.startsWith('__not:'));
  const positiveConditions = showWhen.filter(c => !c.startsWith('__not:'));

  if (Array.isArray(answer)) {
    for (const nc of notConditions) {
      const excludeVal = nc.slice(6);
      if (answer.includes(excludeVal)) return false;
    }
    if (notConditions.length > 0 && positiveConditions.length === 0) return true;
    return positiveConditions.some(c => answer.includes(c));
  }

  // String answer
  return showWhen.some(condition => {
    if (condition.startsWith('__not:')) {
      return answer !== condition.slice(6);
    }
    return condition === answer;
  });
}

export function useQuestionLogic(questions: AssessmentQuestion[], answers: AnswerData) {
  
  const getProcessedQuestion = (question: AssessmentQuestion): AssessmentQuestion => {
    // Handle dynamic title replacement for successor confidence question
    if (question.id === "family-transitionconfidence") {
      // Look for successor details from successor-details question
      const successorDetailsAnswer = answers["successor-details"];
      let firstName = "your successor";
      
      if (successorDetailsAnswer && typeof successorDetailsAnswer === 'object' && 'firstName' in successorDetailsAnswer) {
        const successorData = successorDetailsAnswer as { firstName?: string };
        firstName = successorData.firstName || "your successor";
      }
      
      return {
        ...question,
        title: question.title.replace("{firstName}", firstName)
      };
    }
    
    // Handle dynamic title replacement for timeline realism question
    if (question.id === "timeline-realism") {
      const timelineAnswer = answers["timeline-assessment"];
      let timeframeText = "your chosen timeframe";
      
      if (isDateSliderAnswer(timelineAnswer)) {
        if (timelineAnswer.type === 'date' && timelineAnswer.value instanceof Date) {
          const years = Math.ceil((timelineAnswer.value.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 365));
          timeframeText = years === 1 ? "1 year" : `${years} years`;
        } else if (timelineAnswer.type === 'slider' && typeof timelineAnswer.value === 'number') {
          const years = timelineAnswer.value;
          timeframeText = years === 1 ? "1 year" : `${years} years`;
        }
      }
      
      return {
        ...question,
        title: question.title.replace("{timeframe}", timeframeText)
      };
    }
    
    return question;
  };

  const getVisibleQuestions = (): AssessmentQuestion[] => {
    return questions.filter(question => {
      if (!question.conditionalDisplay) return true;
      
      const cd = question.conditionalDisplay;

      // Compound conditions use the new evaluator directly
      if (isCompoundCondition(cd)) {
        return evaluateConditionalDisplay(cd, answers);
      }

      const { dependsOn, showWhen } = cd;
      const dependentAnswer = answers[dependsOn];
      
      if (!dependentAnswer) return false;
      
      // Special handling for succession-familyharmony question
      if (question.id === "succession-familyharmony") {
        const successorDetailsAnswer = answers["successor-details"];
        if (successorDetailsAnswer && typeof successorDetailsAnswer === 'object' && 'relationship' in successorDetailsAnswer) {
          const successorData = successorDetailsAnswer as { relationship?: string };
          return successorData.relationship === "family-member";
        }
        return false;
      }
      
      // Special handling for family-transitionconfidence question
      if (question.id === "family-transitionconfidence") {
        const successorDetailsAnswer = answers["successor-details"];
        if (successorDetailsAnswer && typeof successorDetailsAnswer === 'object' && 'relationship' in successorDetailsAnswer) {
          const successorData = successorDetailsAnswer as { relationship?: string };
          return successorData.relationship === "family-member";
        }
        return false;
      }
      
      // Special handling for current-estateinclusion question
      if (question.id === "current-estateinclusion") {
        const estateAnswer = answers["current-estateplan"];
        return estateAnswer === "yes-current" || estateAnswer === "yes-outdated";
      }
      
      // Special handling for succession-executorconversation question
      if (question.id === "succession-executorconversation") {
        const estateInclusionAnswer = answers["current-estateinclusion"];
        return estateInclusionAnswer === "yes-defined" || estateInclusionAnswer === "yes-mentioned";
      }
      
      // Handle emotional-gateway text input with keyword matching
      if (dependsOn === "emotional-gateway" && typeof dependentAnswer === 'string') {
        const answerText = dependentAnswer.toLowerCase();
        return showWhen.some(keyword => answerText.includes(keyword.toLowerCase()));
      }

      // Use the unified evaluator (handles contains operator too)
      return evaluateConditionalDisplay(cd, answers);
    }).map(getProcessedQuestion);
  };

  return {
    getVisibleQuestions,
    getProcessedQuestion
  };
}